import {
  MultipleChoice,
  MultipleChoiceGroup,
  MultipleChoiceSet,
  ResponseTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { Alert, Spin, Tabs, Tag, Typography } from 'antd'
import React from 'react'
import { useSelector } from 'react-redux'
import {
  DEFAULT_DEVIATE_COMMENT,
  TemplateQuestionResetParams,
} from 'src/features/templateEdit/common/types'
import {
  MultipleChoiceSetText,
  SelectResponseTableCell,
} from 'src/features/templateEdit/itemAppender/ResponseDropDown/ResponseDropDown.dump'
import { useMultipleChoices } from 'src/features/templateEdit/responseMultipleChoices/hooks/useMultipleChoices'
import { useTemplateSpecificMultipleChoices } from 'src/features/templateEdit/templateSpecificMultipleChoices/hooks/useTemplateSpecificMultipleChoices'
import { BorderColor, White } from 'src/features/theme/KdsThemeColor'
import { RootState } from 'src/state/store'
import { MultipleChoiceAddButton } from './components/MultipleChoiceAddButton/MultipleChoiceAddButton'
import { MultipleChoiceEditButton } from './components/MultipleChoiceEditButton/MultipleChoiceEditButton'

const { Text, Title } = Typography

type MultipleChoiceSectionWithTabsProps = {
  openMultipleChoiceDrawer: (
    group?: MultipleChoiceGroup,
    isTemplateSpecific?: boolean,
  ) => void
  onCloseDropdown: () => void
  dispatchUpdateTemplateNode: (params: TemplateQuestionResetParams) => void
}

export const MultipleChoiceSectionWithTabs: React.FC<
  MultipleChoiceSectionWithTabsProps
> = ({
  openMultipleChoiceDrawer,
  onCloseDropdown,
  dispatchUpdateTemplateNode,
}) => {
  // 従来の選択肢を取得
  const {
    multipleChoiceGroups,
    isError: isMultipleChoicesError,
    isLoading: isMultipleChoicesLoading,
  } = useMultipleChoices()

  const { templateSpecificMultipleChoiceGroups, isError, isLoading } =
    useTemplateSpecificMultipleChoices()
  const multipleChoiceSets = useSelector(
    (state: RootState) =>
      state.multipleChoiceSetsState.multipleChoiceSets.multipleChoiceSets,
  )

  const onClickMultipleChoice = (clickedId: number) => {
    const responseMultipleChoices = [
      ...multipleChoiceGroups,
      ...templateSpecificMultipleChoiceGroups,
    ].filter(({ id }) => id === clickedId)

    dispatchUpdateTemplateNode({
      responseType: ResponseTypeEnum.MULTIPLE_CHOICE,
      responseMultipleChoices: [...responseMultipleChoices],
      responseMultipleChoiceSets: [],
      responseNumbers: [],
      responseTexts: [],
      responseDatetimes: [],
      responseTimeMeasurements: [],
      responseInformations: [],
      responseEmployees: [],
      responseFormulas: [],
      responseGridVariables: [],
      responseLabelChecks: [],
      responseURLs: [],
      answerOption: undefined,
      deviateComment: DEFAULT_DEVIATE_COMMENT,
      deviateNotify: undefined,
      scale: '',
      isShowInformationImages: 0,
      informationImages: [],
    })
  }

  const onClickMultipleChoiceSet = (clickedId: number) => {
    const responseMultipleChoiceSets = multipleChoiceSets.filter(
      ({ id }) => id === clickedId,
      [],
    )
    dispatchUpdateTemplateNode({
      responseType: ResponseTypeEnum.RESPONSE_SET,
      responseMultipleChoiceSets: [...responseMultipleChoiceSets],
      responseMultipleChoices: [],
      responseNumbers: [],
      responseTexts: [],
      responseDatetimes: [],
      responseTimeMeasurements: [],
      responseInformations: [],
      responseEmployees: [],
      responseFormulas: [],
      responseGridVariables: [],
      responseLabelChecks: [],
      responseURLs: [],
      answerOption: undefined,
      deviateComment: DEFAULT_DEVIATE_COMMENT,
      deviateNotify: undefined,
      scale: '',
      isShowInformationImages: 0,
      informationImages: [],
    })
  }

  const tabItems = [
    {
      key: 'template-specific',
      label: 'このひな形限定',
      children: (
        <div style={{ overflow: 'auto', maxHeight: '320px' }}>
          <MultipleChoiceAddButtonArea
            isTemplateSpecific
            openMultipleChoiceDrawer={openMultipleChoiceDrawer}
          />
          {isLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100px',
              }}
            >
              <Spin />
            </div>
          ) : isError ? (
            <div style={{ padding: '16px' }}>
              <Alert
                message="選択肢の取得に失敗しました"
                type="error"
                showIcon
              />
            </div>
          ) : (
            <MultipleChoiceList
              isTemplateSpecific
              groups={templateSpecificMultipleChoiceGroups}
              onClickMultipleChoice={onClickMultipleChoice}
              onCloseDropdown={onCloseDropdown}
              openMultipleChoiceDrawer={openMultipleChoiceDrawer}
            />
          )}
          <MultipleChoiceSetList
            multipleChoiceSets={multipleChoiceSets}
            onClickMultipleChoiceSet={onClickMultipleChoiceSet}
            onCloseDropdown={onCloseDropdown}
          />
        </div>
      ),
    },
    {
      key: 'shared',
      label: '他のひな形でも使用中',
      children: (
        <div style={{ overflow: 'auto', maxHeight: '320px' }}>
          <MultipleChoiceAddButtonArea
            isTemplateSpecific={false}
            openMultipleChoiceDrawer={openMultipleChoiceDrawer}
          />
          {isMultipleChoicesLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100px',
              }}
            >
              <Spin />
            </div>
          ) : isMultipleChoicesError ? (
            <div style={{ padding: '16px' }}>
              <Alert
                message="選択肢の取得に失敗しました"
                type="error"
                showIcon
              />
            </div>
          ) : (
            <MultipleChoiceList
              isTemplateSpecific={false}
              groups={multipleChoiceGroups}
              onClickMultipleChoice={onClickMultipleChoice}
              onCloseDropdown={onCloseDropdown}
              openMultipleChoiceDrawer={openMultipleChoiceDrawer}
            />
          )}
          <MultipleChoiceSetList
            multipleChoiceSets={multipleChoiceSets}
            onClickMultipleChoiceSet={onClickMultipleChoiceSet}
            onCloseDropdown={onCloseDropdown}
          />
        </div>
      ),
    },
  ]

  return (
    <SelectResponseTableCell theme={{ width: '320px', backgroundColor: White }}>
      <div
        style={{
          margin: '16px',
        }}
      >
        <Title level={5} style={{ marginRight: 'auto' }}>
          選択肢
        </Title>
        <Tabs defaultActiveKey="template-specific" items={tabItems} centered />
      </div>
    </SelectResponseTableCell>
  )
}

type MultipleChoiceListProps = {
  isTemplateSpecific: boolean
  groups: Array<MultipleChoiceGroup>
  onClickMultipleChoice: (id: number) => void
  onCloseDropdown: () => void
  openMultipleChoiceDrawer: (
    group?: MultipleChoiceGroup,
    isTemplateSpecific?: boolean,
  ) => void
}

const MultipleChoiceList = ({
  isTemplateSpecific,
  groups,
  onClickMultipleChoice,
  onCloseDropdown,
  openMultipleChoiceDrawer,
}: MultipleChoiceListProps) => (
  <div style={{ width: '320px' }}>
    {groups
      .filter(group => group.id != null)
      .map((multipleChoiceGroup: MultipleChoiceGroup) => (
        <div
          data-testid="multiple-choice-item"
          key={multipleChoiceGroup.id}
          onClick={() => {
            if (multipleChoiceGroup.id) {
              onClickMultipleChoice(multipleChoiceGroup.id)
            }
            onCloseDropdown()
          }}
          style={{
            float: 'left',
            height: '56px',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            flexWrap: 'nowrap',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'sticky',
              left: 0,
              alignSelf: 'center',
              background: 'white',
              marginRight: '1rem',
            }}
          >
            {multipleChoiceGroup.responses.length > 0 && (
              <MultipleChoiceEditButton
                key={multipleChoiceGroup.id}
                multipleChoiceGroup={multipleChoiceGroup}
                openMultipleChoiceDrawer={(group: MultipleChoiceGroup) =>
                  openMultipleChoiceDrawer(group, isTemplateSpecific)
                }
              />
            )}
          </div>
          {multipleChoiceGroup.responses.map(
            (multipleChoice: MultipleChoice) => (
              <Tag key={multipleChoice.id} color={multipleChoice.color}>
                {multipleChoice.response}
              </Tag>
            ),
          )}
        </div>
      ))}
  </div>
)

type MultipleChoiceSetListProps = {
  multipleChoiceSets: MultipleChoiceSet[]
  onClickMultipleChoiceSet: (id: number) => void
  onCloseDropdown: () => void
}

const MultipleChoiceSetList = ({
  multipleChoiceSets,
  onClickMultipleChoiceSet,
  onCloseDropdown,
}: MultipleChoiceSetListProps) => (
  <div style={{ width: '320px' }}>
    <div
      style={{
        float: 'left',
        width: '100%',
        borderTop: `0.5px solid ${BorderColor}`,
        marginTop: '16px',
        padding: '8px 4px',
      }}
    >
      <Text>セット選択肢</Text>
    </div>
    {multipleChoiceSets.map(choiceSet => (
      <div
        key={choiceSet.id}
        onClick={() => {
          if (choiceSet.id) {
            onClickMultipleChoiceSet(choiceSet.id)
          }
          onCloseDropdown()
        }}
        style={{
          height: '40px',
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          flexWrap: 'nowrap',
          position: 'relative',
          paddingLeft: '8px',
        }}
      >
        <MultipleChoiceSetText ellipsis>{choiceSet.name}</MultipleChoiceSetText>
      </div>
    ))}
  </div>
)

type MultipleChoiceAddButtonAreaProps = { isTemplateSpecific: boolean } & Pick<
  MultipleChoiceSectionWithTabsProps,
  'openMultipleChoiceDrawer'
>

const MultipleChoiceAddButtonArea = ({
  isTemplateSpecific,
  openMultipleChoiceDrawer,
}: MultipleChoiceAddButtonAreaProps) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        lineHeight: '40px',
        height: '40px',
      }}
    >
      <MultipleChoiceAddButton
        openMultipleChoiceDrawer={() =>
          openMultipleChoiceDrawer(undefined, isTemplateSpecific)
        }
      />
    </div>
  )
}
